import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import {
  api, type Adjunto, type Caja, type FeeGenerado, type FiduciantePublico, type Honorarios, type LiquidacionFee, type PayloadBeneficiarios,
  type PayloadLiquidacion, type RegistroFees, type TipoCambioSugerido,
} from "@/lib/api";
import { validarArchivo } from "@/lib/adjuntos";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import {
  aPayloadBeneficiarios, aPayloadLiquidacion, agregarBeneficiario, alternarSeleccion, beneficiariosCambiaron, filtrarRegistro, filtrosRegistroIniciales,
  formBeneficiarios, formLiquidacion, opcionesBeneficiario, opcionesMesRegistro, opcionesProveedorRegistro, puedeElegir, quitarBeneficiario,
  totalPorCobrar, totalesRegistro, validarBeneficiarios, validarLiquidacion,
  type ErroresLiquidacion, type FiltrosRegistro, type FormLiquidacion, type LineaBeneficiario,
} from "@/lib/honorarios";

const SIN_ERRORES: ErroresLiquidacion = {};
type Dialogo = { tipo: "liquidar" } | { tipo: "anular"; liquidacion: LiquidacionFee } | { tipo: "factura"; id: number } | null;

/**
 * Lógica de Honorarios (sin JSX, sólo admin): el registro de los fees generados (Mati, 2026-11-02), desde donde se eligen
 * los A por cobrar y se liquidan juntos con su factura; quiénes reciben el fee B y las liquidaciones hechas
 * (`docs/modulo-honorarios.md`).
 */
export function useHonorariosTab() {
  const { fid } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/honorarios`;
  const honorarios = useApiResource<Honorarios>(base);
  const fiduciantes = useApiResource<FiduciantePublico[]>(`/api/fideicomisos/${fid}/fiduciantes`);
  const cajas = useApiResource<Caja[]>(`/api/fideicomisos/${fid}/cajas`);
  const tcSugerido = useApiResource<TipoCambioSugerido>(`/api/fideicomisos/${fid}/tipo-cambio-sugerido`);
  const registro = useApiResource<RegistroFees>(`${base}/registro`);
  const h = honorarios.data ?? null;
  const recargar = () => {
    honorarios.recargar();
    registro.recargar();
  };

  // Registro de fees: filtros y los A por cobrar elegidos para liquidar (fee_id).
  const [filtros, setFiltros] = useState<FiltrosRegistro>(filtrosRegistroIniciales);
  const [seleccion, setSeleccion] = useState<number[]>([]);
  const items = registro.data?.items ?? [];
  const filtrados = filtrarRegistro(items, filtros);
  const elegidos = items.filter((i) => seleccion.includes(i.fee_id));

  // Quiénes reciben el fee B: null = lo guardado, sin tocar.
  const [lineasEditadas, setLineasEditadas] = useState<LineaBeneficiario[] | null>(null);
  const lineas = lineasEditadas ?? formBeneficiarios(h?.beneficiarios ?? []);
  const errorLineas = validarBeneficiarios(lineas);
  const accionBeneficiarios = useAsyncAction((datos: PayloadBeneficiarios) => api.put<Honorarios>(`${base}/beneficiarios`, datos));

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormLiquidacion>(() => formLiquidacion("ARS", null));
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [factura, setFactura] = useState<File | null>(null);
  const [errorFactura, setErrorFactura] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const accionLiquidar = useAsyncAction((datos: PayloadLiquidacion) => api.post<LiquidacionFee>(`${base}/liquidaciones`, datos));
  const accionAnular = useAsyncAction((id: number) => api.delete<void>(`${base}/liquidaciones/${id}`).then(() => true));
  const accionSubir = useAsyncAction((id: number, archivo: File) => {
    const datos = new FormData();
    datos.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/liquidaciones/${id}/adjuntos`, datos);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const errores = validarLiquidacion(form);
  const cerrar = () => setDialogo(null);
  const listaCajas = cajas.data ?? [];

  return {
    cargando: !h && honorarios.loading,
    errorCarga: h ? null : (honorarios.error?.message ?? null),
    reintentar: recargar,
    honorarios: h,
    aviso,

    // ---------------------------------------------------------------- registro de fees
    cargandoRegistro: !registro.data && registro.loading,
    errorRegistro: registro.data ? null : (registro.error?.message ?? null),
    filtros,
    cambiarFiltro: <K extends keyof FiltrosRegistro>(campo: K, valor: FiltrosRegistro[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    hayFiltros: JSON.stringify(filtros) !== JSON.stringify(filtrosRegistroIniciales()),
    limpiarFiltros: () => setFiltros(filtrosRegistroIniciales()),
    opcionesMes: opcionesMesRegistro(items),
    opcionesProveedor: opcionesProveedorRegistro(items),
    fees: filtrados,
    totales: totalesRegistro(filtrados),
    elegido: (i: FeeGenerado) => seleccion.includes(i.fee_id),
    puedeElegir: (i: FeeGenerado) => seleccion.includes(i.fee_id) || puedeElegir(i, seleccion, items),
    alternar: (i: FeeGenerado) => setSeleccion((s) => alternarSeleccion(s, i.fee_id)),
    elegidos,
    totalElegidos: totalPorCobrar(elegidos),
    monedaElegidos: elegidos[0]?.moneda ?? "ARS",
    rutaGasto: (i: FeeGenerado) => `/gastos/${i.gasto_id}`,

    // ---------------------------------------------------------------- fee B
    lineas,
    opcionesBeneficiario: opcionesBeneficiario(fiduciantes.data ?? [], lineas),
    agregar: (valor: string) => {
      const f = (fiduciantes.data ?? []).find((x) => String(x.id) === valor);
      if (f) setLineasEditadas(agregarBeneficiario(lineas, f));
    },
    quitar: (fiduciante_id: number) => setLineasEditadas(quitarBeneficiario(lineas, fiduciante_id)),
    cambiarPorcentaje: (fiduciante_id: number, porcentaje: string) =>
      setLineasEditadas(lineas.map((l) => (l.fiduciante_id === fiduciante_id ? { ...l, porcentaje } : l))),
    cambiaron: h ? beneficiariosCambiaron(lineas, h.beneficiarios) : false,
    errorLineas: lineasEditadas ? errorLineas : null,
    guardarBeneficiarios: async () => {
      if (errorLineas) return;
      if (await accionBeneficiarios.run(aPayloadBeneficiarios(lineas))) {
        setLineasEditadas(null);
        recargar();
      }
    },
    descartarBeneficiarios: () => {
      setLineasEditadas(null);
      accionBeneficiarios.limpiarError();
    },
    guardandoBeneficiarios: accionBeneficiarios.loading,
    errorBeneficiarios: accionBeneficiarios.error,

    // ---------------------------------------------------------------- liquidación de los fees A elegidos
    dialogo,
    cerrar,
    abrirLiquidar: () => {
      const [primero] = elegidos;
      if (!primero) return;
      setForm(formLiquidacion(primero.moneda, tcSugerido.data?.tipo_cambio, cajaPorDefecto(listaCajas)));
      setMostrarErrores(false);
      setFactura(null);
      setErrorFactura(null);
      setAviso(null);
      accionLiquidar.limpiarError();
      setDialogo({ tipo: "liquidar" });
    },
    form,
    opcionesCaja: hayVariasCajas(listaCajas) ? opcionesCaja(listaCajas) : null,
    cambiar: <K extends keyof FormLiquidacion>(campo: K, valor: FormLiquidacion[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    factura,
    elegirFactura: (archivos: File[]) => {
      const archivo = archivos[0] ?? null;
      const error = archivo ? validarArchivo(archivo) : null;
      setErrorFactura(error);
      setFactura(error ? null : archivo);
    },
    errorFactura,
    liquidar: async (e: FormEvent) => {
      e.preventDefault();
      setMostrarErrores(true);
      if (Object.keys(errores).length > 0) return;
      const liq = await accionLiquidar.run(aPayloadLiquidacion(form, seleccion));
      if (!liq) return;
      if (factura && !(await accionSubir.run(liq.id, factura))) {
        setAviso("La liquidación quedó, pero no se pudo subir la factura: subila desde «Factura».");
      }
      setSeleccion([]);
      cerrar();
      recargar();
    },
    liquidando: accionLiquidar.loading || accionSubir.loading,
    errorLiquidar: accionLiquidar.error,

    abrirAnular: (liquidacion: LiquidacionFee) => {
      accionAnular.limpiarError();
      setDialogo({ tipo: "anular", liquidacion });
    },
    confirmarAnular: async () => {
      if (dialogo?.tipo !== "anular") return;
      if (await accionAnular.run(dialogo.liquidacion.id)) {
        cerrar();
        recargar();
      }
    },
    anulando: accionAnular.loading,
    errorAnular: accionAnular.error,

    // ---------------------------------------------------------------- factura de una liquidación ya hecha
    abrirFactura: (liquidacion: Pick<LiquidacionFee, "id">) => {
      accionSubir.limpiarError();
      accionBorrarAdjunto.limpiarError();
      setDialogo({ tipo: "factura", id: liquidacion.id });
    },
    liquidacionFactura: dialogo?.tipo === "factura" ? (h?.liquidaciones.find((l) => l.id === dialogo.id) ?? null) : null,
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirFactura: async (archivo: File) => {
      if (dialogo?.tipo === "factura" && (await accionSubir.run(dialogo.id, archivo))) honorarios.recargar();
    },
    borrarFactura: async (a: Adjunto) => {
      if (await accionBorrarAdjunto.run(a.id)) honorarios.recargar();
    },
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
